import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, setDoc, doc, query, orderBy, getDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

function Hours() {
    const { user, userDoc } = useAuth();
    const isAdmin = userDoc && userDoc.admin;
    const [records, setRecords] = useState([]);
    const [nameMap, setNameMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');

    // Form state
    const [recordDate, setRecordDate] = useState('');
    const [name, setName] = useState('');
    const [taxYear, setTaxYear] = useState((new Date().getFullYear() - 1).toString());
    const [classifiedTransactions, setClassifiedTransactions] = useState('');
    const [extractedBankStatement, setExtractedBankStatement] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Edit state
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});

    // Conversion rates (admin only)
    const [txPerMin, setTxPerMin] = useState(2);
    const [minPerStmt, setMinPerStmt] = useState(10);
    const [editingRates, setEditingRates] = useState(false);
    const [editTxPerMin, setEditTxPerMin] = useState(2);
    const [editMinPerStmt, setEditMinPerStmt] = useState(10);

    // Name mapping state
    const [showMappingForm, setShowMappingForm] = useState(false);
    const [newShort, setNewShort] = useState('');
    const [newFull, setNewFull] = useState('');
    const [editingShort, setEditingShort] = useState(null);
    const [editFull, setEditFull] = useState('');
    const [pasteText, setPasteText] = useState('');
    const [selectedMappings, setSelectedMappings] = useState(new Set());
    const [mappingSearch, setMappingSearch] = useState('');
    const [mappingConflicts, setMappingConflicts] = useState([]);
    const [pendingMappingUpdates, setPendingMappingUpdates] = useState(null);
    const [bulkAddProgress, setBulkAddProgress] = useState(0);
    const [isBulkAdding, setIsBulkAdding] = useState(false);

    // Filters
    const [filterName, setFilterName] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
    const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
    const [datePickerStep, setDatePickerStep] = useState('start');

    const getUserDisplayName = () => {
        if (userDoc?.name) return userDoc.name;
        if (user?.displayName) return user.displayName;
        if (user?.email) return user.email.split('@')[0];
        return 'Unknown';
    };

    const loadNameMappings = async () => {
        try {
            const snapshot = await getDocs(collection(db, 'nameMappings'));
            const map = {};
            snapshot.docs.forEach(d => {
                map[d.id.toLowerCase()] = { short: d.id, fullName: d.data().fullName };
            });
            setNameMap(map);
            return map;
        } catch (error) {
            console.error('Error loading name mappings:', error);
            return {};
        }
    };

    const loadRecords = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, 'hours'), orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);
            const data = snapshot.docs.map(d => ({
                id: d.id,
                ...d.data(),
                recordDate: d.data().recordDate?.toDate?.() || d.data().createdAt?.toDate?.() || new Date(d.data().createdAt),
                createdAt: d.data().createdAt?.toDate?.() || new Date(d.data().createdAt),
                lastEditAt: d.data().lastEditAt?.toDate?.() || null,
            }));
            setRecords(data);
        } catch (error) {
            console.error('Error loading records:', error);
            setMessage('Failed to load records.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const loadRates = async () => {
            try {
                const snap = await getDoc(doc(db, 'settings', 'conversionRates'));
                if (snap.exists()) {
                    const d = snap.data();
                    if (d.txPerMin) { setTxPerMin(d.txPerMin); setEditTxPerMin(d.txPerMin); }
                    if (d.minPerStmt) { setMinPerStmt(d.minPerStmt); setEditMinPerStmt(d.minPerStmt); }
                }
            } catch (e) { console.error('Error loading rates:', e); }
        };
        Promise.all([loadNameMappings(), loadRecords(), loadRates()]);
    }, []);

    const resolveFullName = (shortName) => {
        if (!shortName) return '—';
        const entry = nameMap[shortName.toLowerCase()];
        return entry ? entry.fullName : shortName;
    };

    const calcMinutes = (classifiedTx, extractedStmt) => {
        const txCount = parseFloat(classifiedTx) || 0;
        const stmtCount = parseFloat(extractedStmt) || 0;
        const txMinutes = txCount / txPerMin;
        const stmtMinutes = stmtCount * minPerStmt;
        return { txMinutes, stmtMinutes, totalMinutes: txMinutes + stmtMinutes };
    };

    const formatTime = (minutes) => {
        if (minutes === 0) return '—';
        const hrs = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        if (hrs > 0) return `${hrs}h ${mins}m`;
        return `${mins}m`;
    };

    const formatDate = (date) => {
        if (!date) return '—';
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
        }).format(date);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !taxYear.trim()) {
            setMessage('Name and Tax Year are required.');
            return;
        }
        setIsSubmitting(true);
        try {
            const dateToStore = recordDate
                ? Timestamp.fromDate(new Date(recordDate + 'T00:00:00'))
                : Timestamp.now();
            await addDoc(collection(db, 'hours'), {
                recordDate: dateToStore,
                name: name.trim().toUpperCase(),
                taxYear: taxYear.trim(),
                classifiedTransactions: classifiedTransactions.trim(),
                extractedBankStatement: extractedBankStatement.trim(),
                createdAt: Timestamp.now(),
                lastEditBy: getUserDisplayName(),
                lastEditAt: Timestamp.now(),
            });
            setRecordDate('');
            setName('');
            setTaxYear((new Date().getFullYear() - 1).toString());
            setClassifiedTransactions('');
            setExtractedBankStatement('');
            setMessage('Record added successfully!');
            setTimeout(() => setMessage(''), 3000);
            await loadRecords();
        } catch (error) {
            console.error('Error adding record:', error);
            setMessage('Failed to add record: ' + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const startEdit = (record) => {
        setEditingId(record.id);
        const d = record.recordDate;
        const dateStr = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '';
        setEditForm({
            recordDate: dateStr,
            name: record.name,
            taxYear: record.taxYear,
            classifiedTransactions: record.classifiedTransactions || '',
            extractedBankStatement: record.extractedBankStatement || '',
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditForm({});
    };

    const saveEdit = async (id) => {
        try {
            const editDateToStore = editForm.recordDate
                ? Timestamp.fromDate(new Date(editForm.recordDate + 'T00:00:00'))
                : Timestamp.now();
            await updateDoc(doc(db, 'hours', id), {
                recordDate: editDateToStore,
                name: editForm.name.trim().toUpperCase(),
                taxYear: editForm.taxYear.trim(),
                classifiedTransactions: editForm.classifiedTransactions.trim(),
                extractedBankStatement: editForm.extractedBankStatement.trim(),
                lastEditBy: getUserDisplayName(),
                lastEditAt: Timestamp.now(),
            });
            setEditingId(null);
            setEditForm({});
            setMessage('Record updated!');
            setTimeout(() => setMessage(''), 3000);
            await loadRecords();
        } catch (error) {
            console.error('Error updating record:', error);
            setMessage('Failed to update record: ' + error.message);
        }
    };

    const deleteRecord = async (id) => {
        if (!window.confirm('Are you sure you want to delete this record?')) return;
        try {
            await deleteDoc(doc(db, 'hours', id));
            setMessage('Record deleted.');
            setTimeout(() => setMessage(''), 3000);
            await loadRecords();
        } catch (error) {
            console.error('Error deleting record:', error);
            setMessage('Failed to delete record: ' + error.message);
        }
    };

    // Name mapping CRUD
    const addMapping = async () => {
        if (!newShort.trim() || !newFull.trim()) {
            setMessage('Both short name and full name are required.');
            return;
        }
        if (newShort.trim().includes('/')) {
            setMessage('Short name cannot contain "/".');
            return;
        }
        try {
            const short = newShort.trim();
            const full = newFull.trim();
            await setDoc(doc(db, 'nameMappings', short), { fullName: full });
            setNameMap(prev => ({ ...prev, [short.toLowerCase()]: { short, fullName: full } }));
            setNewShort('');
            setNewFull('');
            setMessage('Name mapping added!');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            setMessage('Failed to add mapping: ' + error.message);
        }
    };

    const handleBulkAdd = async () => {
        const lines = pasteText.trim().split('\n').filter(l => l.trim());
        if (lines.length === 0) return;
        setIsBulkAdding(true);
        setMessage('Checking for conflicts...');
        setBulkAddProgress(0);
        const entries = [];
        for (const line of lines) {
            const parts = line.split('\t');
            if (parts.length >= 2 && parts[0].trim() && parts[1].trim() && !parts[0].trim().includes('/')) {
                entries.push({ shortName: parts[0].trim(), fullName: parts[1].trim() });
            }
        }
        if (entries.length === 0) {
            setMessage('No valid entries found. Use tab-separated format: ShortName\tFullName');
            setIsBulkAdding(false);
            return;
        }
        const conflictsList = [];
        const toAdd = [];
        let skipped = 0;
        for (let i = 0; i < entries.length; i++) {
            const entry = entries[i];
            const existingEntry = nameMap[entry.shortName.toLowerCase()];
            if (existingEntry) {
                if (existingEntry.fullName !== entry.fullName) {
                    conflictsList.push({ shortName: entry.shortName, existingFullName: existingEntry.fullName, newFullName: entry.fullName });
                } else { skipped++; }
            } else { toAdd.push(entry); }
            setBulkAddProgress(((i + 1) / entries.length) * 50);
        }
        if (conflictsList.length > 0) {
            setMappingConflicts(conflictsList);
            setPendingMappingUpdates({ toAdd, skipped });
            setMessage(`${conflictsList.length} conflict(s) detected. Please choose how to handle each.`);
            setIsBulkAdding(false);
            setBulkAddProgress(0);
            return;
        }
        await finalizeBulkAdd(toAdd, skipped);
    };

    const finalizeBulkAdd = async (toAdd, skipped) => {
        setIsBulkAdding(true);
        setBulkAddProgress(50);
        try {
            const results = await Promise.all(
                toAdd.map(entry =>
                    setDoc(doc(db, 'nameMappings', entry.shortName), { fullName: entry.fullName })
                        .then(() => ({ success: true, entry }))
                        .catch(() => ({ success: false, entry }))
                )
            );
            const added = results.filter(r => r.success).length;
            setBulkAddProgress(100);
            setNameMap(prev => {
                const next = { ...prev };
                results.filter(r => r.success).forEach(r => {
                    next[r.entry.shortName.toLowerCase()] = { short: r.entry.shortName, fullName: r.entry.fullName };
                });
                return next;
            });
            setPasteText('');
            setMappingConflicts([]);
            setPendingMappingUpdates(null);
            setMessage(`Added ${added} mapping${added !== 1 ? 's' : ''}${skipped > 0 ? `, ${skipped} unchanged` : ''}.`);
            setTimeout(() => setMessage(''), 4000);
        } catch (error) {
            setMessage('Failed to add mappings: ' + error.message);
        }
        setIsBulkAdding(false);
        setBulkAddProgress(0);
    };

    const handleResolveMappingConflict = async (index, choice) => {
        const conflict = mappingConflicts[index];
        const { toAdd, skipped } = pendingMappingUpdates;
        let newSkipped = skipped;
        if (choice === 'update') { toAdd.push({ shortName: conflict.shortName, fullName: conflict.newFullName }); }
        else { newSkipped++; }
        const remaining = mappingConflicts.filter((_, i) => i !== index);
        setMappingConflicts(remaining);
        setPendingMappingUpdates({ toAdd, skipped: newSkipped });
        if (remaining.length === 0) { await finalizeBulkAdd(toAdd, newSkipped); }
        else { setMessage(`${remaining.length} conflict(s) remaining.`); }
    };

    const handleResolveAllMappingConflicts = async (choice) => {
        const { toAdd, skipped } = pendingMappingUpdates;
        let newSkipped = skipped;
        if (choice === 'update') { mappingConflicts.forEach(c => toAdd.push({ shortName: c.shortName, fullName: c.newFullName })); }
        else { newSkipped += mappingConflicts.length; }
        setMappingConflicts([]);
        await finalizeBulkAdd(toAdd, newSkipped);
    };

    const updateMapping = async (shortName) => {
        if (!editFull.trim()) return;
        try {
            const full = editFull.trim();
            await setDoc(doc(db, 'nameMappings', shortName), { fullName: full });
            setNameMap(prev => ({ ...prev, [shortName.toLowerCase()]: { short: shortName, fullName: full } }));
            setEditingShort(null);
            setEditFull('');
            setMessage('Mapping updated!');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            setMessage('Failed to update mapping: ' + error.message);
        }
    };

    const deleteMapping = async (shortName) => {
        if (!window.confirm(`Delete mapping for "${shortName}"?`)) return;
        try {
            await deleteDoc(doc(db, 'nameMappings', shortName));
            setNameMap(prev => { const next = { ...prev }; delete next[shortName.toLowerCase()]; return next; });
            setSelectedMappings(prev => { const next = new Set(prev); next.delete(shortName); return next; });
            setMessage('Mapping deleted.');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            setMessage('Failed to delete mapping: ' + error.message);
        }
    };

    const bulkDeleteMappings = async () => {
        if (selectedMappings.size === 0) return;
        if (!window.confirm(`Delete ${selectedMappings.size} selected mapping${selectedMappings.size !== 1 ? 's' : ''}?`)) return;
        try {
            await Promise.all(Array.from(selectedMappings).map(shortName => deleteDoc(doc(db, 'nameMappings', shortName))));
            setNameMap(prev => {
                const next = { ...prev };
                selectedMappings.forEach(s => delete next[s.toLowerCase()]);
                return next;
            });
            setMessage(`Deleted ${selectedMappings.size} mapping${selectedMappings.size !== 1 ? 's' : ''}.`);
            setSelectedMappings(new Set());
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            setMessage('Failed to delete mappings: ' + error.message);
        }
    };

    const toggleMappingSelection = (shortName) => {
        setSelectedMappings(prev => {
            const next = new Set(prev);
            if (next.has(shortName)) next.delete(shortName); else next.add(shortName);
            return next;
        });
    };

    const filteredMappings = Object.entries(nameMap)
        .filter(([key, val]) => {
            if (!mappingSearch) return true;
            const q = mappingSearch.toLowerCase();
            return val.short.toLowerCase().includes(q) || val.fullName.toLowerCase().includes(q);
        })
        .sort((a, b) => a[0].localeCompare(b[0]));

    const toggleAllMappings = () => {
        const visibleKeys = filteredMappings.map(([, val]) => val.short);
        const allSelected = visibleKeys.every(k => selectedMappings.has(k));
        if (allSelected) {
            setSelectedMappings(prev => { const next = new Set(prev); visibleKeys.forEach(k => next.delete(k)); return next; });
        } else {
            setSelectedMappings(prev => new Set([...prev, ...visibleKeys]));
        }
    };

    // Filter & aggregate
    const filteredRecords = records.filter(r => {
        if (filterName) {
            const q = filterName.toLowerCase();
            const shortMatch = r.name?.toLowerCase().includes(q);
            const fullMatch = resolveFullName(r.name).toLowerCase().includes(q);
            if (!shortMatch && !fullMatch) return false;
        }
        if (filterYear && r.taxYear !== filterYear) return false;
        if (filterDateFrom) {
            const from = new Date(filterDateFrom + 'T00:00:00');
            if (r.recordDate < from) return false;
        }
        if (filterDateTo) {
            const to = new Date(filterDateTo + 'T23:59:59');
            if (r.recordDate > to) return false;
        }
        return true;
    });

    const sortedRecords = [...filteredRecords].sort((a, b) => b.recordDate - a.recordDate);

    const grandTotals = sortedRecords.reduce((acc, r) => {
        const tx = parseFloat(r.classifiedTransactions) || 0;
        const stmt = parseFloat(r.extractedBankStatement) || 0;
        const { txMinutes, stmtMinutes, totalMinutes } = calcMinutes(tx, stmt);
        return {
            tx: acc.tx + tx,
            stmt: acc.stmt + stmt,
            txMin: acc.txMin + txMinutes,
            stmtMin: acc.stmtMin + stmtMinutes,
            totalMin: acc.totalMin + totalMinutes,
        };
    }, { tx: 0, stmt: 0, txMin: 0, stmtMin: 0, totalMin: 0 });

    const uniqueYears = [...new Set(records.map(r => r.taxYear))].sort().reverse();

    return (
        <div className="container">
            <div className="page-header">
                <h2>Hours Tracking</h2>
                <p>Log classified transactions and extracted bank statements</p>
            </div>

            {/* Add Record Form */}
            <div className="card" style={{ marginBottom: '2rem' }}>
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '1.5rem', fontWeight: 600 }}>Add New Record</h3>
                <form onSubmit={handleSubmit}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                        <div className="form-group">
                            <label>Date</label>
                            <input className="form-control" type="date" value={recordDate} onChange={e => setRecordDate(e.target.value)} />
                            <small style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>Leave blank for today's date</small>
                        </div>
                        <div className="form-group">
                            <label>Name *</label>
                            <input className="form-control" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Enter name" required />
                        </div>
                        <div className="form-group">
                            <label>Tax Year *</label>
                            <input className="form-control" type="text" value={taxYear} onChange={e => setTaxYear(e.target.value)} placeholder="e.g. 2025" required />
                        </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                        <div className="form-group">
                            <label>Classified Transactions</label>
                            <input className="form-control" type="text" value={classifiedTransactions} onChange={e => setClassifiedTransactions(e.target.value)} placeholder="Enter count" />
                        </div>
                        <div className="form-group">
                            <label>Extracted Bank Statements</label>
                            <input className="form-control" type="text" value={extractedBankStatement} onChange={e => setExtractedBankStatement(e.target.value)} placeholder="Enter count" />
                        </div>
                    </div>
                    <button className="btn btn-primary" type="submit" disabled={isSubmitting} style={{ marginTop: '0.5rem' }}>
                        {isSubmitting ? 'Adding...' : 'Add Record'}
                    </button>
                </form>
            </div>

            {message && (
                <div className={`alert ${message.includes('Failed') || message.includes('required') ? 'alert-error' : 'alert-success'}`}>
                    {message}
                </div>
            )}

            {/* Name Mapping Management - Admin only */}
            {isAdmin && <div className="card" style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showMappingForm ? '1rem' : 0 }}>
                    <h3 style={{ color: 'var(--color-primary)', fontWeight: 600 }}>Name Mappings</h3>
                    <button className="btn btn-secondary" onClick={() => setShowMappingForm(!showMappingForm)}>
                        {showMappingForm ? 'Hide' : 'Manage Mappings'}
                    </button>
                </div>

                {showMappingForm && (
                    <>
                        {/* Bulk paste from Excel */}
                        <div className="form-group" style={{ marginBottom: '1rem' }}>
                            <label>Paste from Excel <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>(two columns: Short Name → Full Name, tab-separated)</span></label>
                            <textarea
                                className="form-control"
                                value={pasteText}
                                onChange={e => setPasteText(e.target.value)}
                                placeholder={'JD\tJohn Doe\nJS\tJane Smith'}
                                rows={4}
                                style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                disabled={isBulkAdding}
                            />
                            <button className="btn btn-primary" style={{ marginTop: '0.5rem' }} disabled={!pasteText.trim() || isBulkAdding} onClick={handleBulkAdd}>
                                {isBulkAdding ? 'Processing...' : 'Bulk Add'}
                            </button>
                        </div>

                        {/* Progress bar */}
                        {isBulkAdding && (
                            <div style={{ marginBottom: '1rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--color-text-muted)', marginBottom: '0.25rem' }}>
                                    <span>Processing...</span>
                                    <span>{Math.round(bulkAddProgress)}%</span>
                                </div>
                                <div style={{ width: '100%', backgroundColor: 'var(--color-border)', borderRadius: '8px', overflow: 'hidden' }}>
                                    <div style={{ width: `${bulkAddProgress}%`, height: '6px', background: 'var(--gradient-primary)', transition: 'width 0.1s linear' }}></div>
                                </div>
                            </div>
                        )}

                        {/* Conflict resolution UI */}
                        {mappingConflicts.length > 0 && (
                            <div style={{ marginBottom: '1rem', borderLeft: '4px solid #ffc107', background: '#fff3cd', borderRadius: '8px', padding: '1rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                                    <h4 style={{ color: '#856404', margin: 0, fontSize: '1rem' }}>Conflicts Detected ({mappingConflicts.length})</h4>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <button className="btn btn-secondary btn-sm" onClick={() => handleResolveAllMappingConflicts('keep')}>Keep All Existing</button>
                                        <button className="btn btn-success btn-sm" onClick={() => handleResolveAllMappingConflicts('update')}>Update All to New</button>
                                    </div>
                                </div>
                                <p style={{ color: '#856404', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                                    These short names already exist with different full names. Choose to keep the existing or update to new.
                                </p>
                                {mappingConflicts.map((conflict, idx) => (
                                    <div key={idx} style={{ padding: '0.75rem', marginBottom: '0.5rem', borderRadius: '8px', border: '1px solid #ffc107', background: 'white' }}>
                                        <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem' }}>{conflict.shortName}</div>
                                        <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                                            <div style={{ flex: 1 }}>
                                                <span style={{ color: '#856404', fontWeight: 500 }}>Existing: </span>
                                                <span style={{ fontFamily: 'monospace' }}>{conflict.existingFullName}</span>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <span style={{ color: 'var(--color-success)', fontWeight: 500 }}>New: </span>
                                                <span style={{ fontFamily: 'monospace' }}>{conflict.newFullName}</span>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <button className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={() => handleResolveMappingConflict(idx, 'keep')}>Keep Existing</button>
                                            <button className="btn btn-success btn-sm" style={{ flex: 1 }} onClick={() => handleResolveMappingConflict(idx, 'update')}>Update to New</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {Object.keys(nameMap).length > 0 && (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                                    <div className="form-group" style={{ marginBottom: 0, flex: '1 1 200px' }}>
                                        <input className="form-control" type="text" value={mappingSearch} onChange={e => setMappingSearch(e.target.value)} placeholder="Search by short name or full name..." style={{ padding: '0.4rem 0.6rem', fontSize: '0.85rem' }} />
                                    </div>
                                    {selectedMappings.size > 0 && (
                                        <>
                                            <span style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)' }}>{selectedMappings.size} selected</span>
                                            <button className="btn btn-danger btn-sm" onClick={bulkDeleteMappings}>Delete Selected</button>
                                        </>
                                    )}
                                </div>
                                <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: '6px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                        <thead style={{ position: 'sticky', top: 0, background: 'var(--color-surface, #fff)', zIndex: 1 }}>
                                            <tr style={{ borderBottom: '2px solid var(--color-border)', textAlign: 'left' }}>
                                                <th style={{ padding: '0.5rem', width: '40px' }}>
                                                    <input type="checkbox" checked={filteredMappings.length > 0 && filteredMappings.every(([, val]) => selectedMappings.has(val.short))} onChange={toggleAllMappings} />
                                                </th>
                                                <th style={{ padding: '0.5rem' }}>Short Name</th>
                                                <th style={{ padding: '0.5rem' }}>Full Name</th>
                                                <th style={{ padding: '0.5rem', width: '120px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredMappings.map(([key, val]) => (
                                                <tr key={key} style={{ borderBottom: '1px solid var(--color-border)', background: selectedMappings.has(val.short) ? 'rgba(102,126,234,0.06)' : undefined }}>
                                                    <td style={{ padding: '0.5rem' }}>
                                                        <input type="checkbox" checked={selectedMappings.has(val.short)} onChange={() => toggleMappingSelection(val.short)} />
                                                    </td>
                                                    <td style={{ padding: '0.5rem', fontWeight: 500 }}>{val.short}</td>
                                                    <td style={{ padding: '0.5rem' }}>
                                                        {editingShort === val.short ? (
                                                            <input className="form-control" style={{ padding: '0.3rem' }} value={editFull} onChange={e => setEditFull(e.target.value)} />
                                                        ) : val.fullName}
                                                    </td>
                                                    <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>
                                                        {editingShort === val.short ? (
                                                            <>
                                                                <button className="btn btn-link btn-link-primary" onClick={() => updateMapping(val.short)}>Save</button>
                                                                <button className="btn btn-link" style={{ color: 'var(--color-gray)' }} onClick={() => setEditingShort(null)}>Cancel</button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <button className="btn btn-link btn-link-primary" onClick={() => { setEditingShort(val.short); setEditFull(val.fullName); }}>Edit</button>
                                                                <button className="btn btn-link btn-link-danger" onClick={() => deleteMapping(val.short)}>Delete</button>
                                                            </>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>}

            {/* Records Table */}
            <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3 style={{ color: 'var(--color-primary)', fontWeight: 600, margin: 0 }}>
                        Records ({sortedRecords.length} {sortedRecords.length === 1 ? 'entry' : 'entries'})
                    </h3>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        {isAdmin && sortedRecords.length > 0 && (
                            <button className="btn btn-primary btn-sm" onClick={() => {
                                const headers = isAdmin
                                    ? ['Date', 'Name', 'Full Name', 'Tax Year', 'Transactions', 'Tx Time', 'Statements', 'Stmt Time', 'Total Time']
                                    : ['Date', 'Name', 'Tax Year', 'Transactions', 'Statements'];
                                const rows = sortedRecords.map(row => {
                                    const tx = parseFloat(row.classifiedTransactions) || 0;
                                    const stmt = parseFloat(row.extractedBankStatement) || 0;
                                    const { txMinutes, stmtMinutes, totalMinutes } = calcMinutes(tx, stmt);
                                    if (isAdmin) {
                                        return [formatDate(row.recordDate), row.name, resolveFullName(row.name), row.taxYear, tx || '', formatTime(txMinutes), stmt || '', formatTime(stmtMinutes), formatTime(totalMinutes)].join('\t');
                                    }
                                    return [formatDate(row.recordDate), row.name, row.taxYear, tx || '', stmt || ''].join('\t');
                                });
                                let text;
                                if (isAdmin) {
                                    const totalRow = ['Grand Total', '', '', '', grandTotals.tx || '', formatTime(grandTotals.txMin), grandTotals.stmt || '', formatTime(grandTotals.stmtMin), formatTime(grandTotals.totalMin)].join('\t');
                                    text = [headers.join('\t'), ...rows, totalRow].join('\n');
                                } else {
                                    const totalRow = ['Grand Total', '', '', grandTotals.tx || '', grandTotals.stmt || ''].join('\t');
                                    text = [headers.join('\t'), ...rows, totalRow].join('\n');
                                }
                                navigator.clipboard.writeText(text).then(() => {
                                    setMessage('Copied to clipboard!');
                                    setTimeout(() => setMessage(''), 2000);
                                });
                            }}>
                                Copy to Excel
                            </button>
                        )}
                        <button className="btn btn-secondary btn-sm" onClick={loadRecords} disabled={loading}>
                            {loading ? 'Loading...' : 'Refresh'}
                        </button>
                        {(filterName || filterYear || filterDateFrom || filterDateTo) && (
                            <button className="btn btn-secondary btn-sm" onClick={() => { setFilterName(''); setFilterYear(''); setFilterDateFrom(''); setFilterDateTo(''); setShowDatePicker(false); setDatePickerStep('start'); }}>
                                Clear Filters
                            </button>
                        )}
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
                    <div className="form-group" style={{ marginBottom: 0, flex: '2 1 200px' }}>
                        <label>Search by Name</label>
                        <input className="form-control" type="text" value={filterName} onChange={e => setFilterName(e.target.value)} placeholder="Search by short name or full name..." />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0, flex: '1 1 120px' }}>
                        <label>Tax Year</label>
                        <select className="form-control" value={filterYear} onChange={e => setFilterYear(e.target.value)}>
                            <option value="">All Years</option>
                            {uniqueYears.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0, flex: '1.5 1 200px', position: 'relative' }}>
                        <label>Date Range</label>
                        <div
                            className="form-control"
                            onClick={() => setShowDatePicker(!showDatePicker)}
                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none', minHeight: '38px', color: (filterDateFrom || filterDateTo) ? 'inherit' : 'var(--color-text-muted)' }}
                        >
                            <span>
                                {filterDateFrom || filterDateTo
                                    ? `${filterDateFrom ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(filterDateFrom + 'T00:00:00')) : 'Any'} – ${filterDateTo ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(filterDateTo + 'T00:00:00')) : 'Any'}`
                                    : 'All dates'}
                            </span>
                            <span style={{ fontSize: '0.7rem', marginLeft: '0.5rem' }}>{showDatePicker ? '▲' : '▼'}</span>
                        </div>
                        {showDatePicker && (() => {
                            const DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
                            const renderMonth = (year, month) => {
                                const firstDay = new Date(year, month, 1).getDay();
                                const daysInMonth = new Date(year, month + 1, 0).getDate();
                                const weeks = [];
                                let week = new Array(firstDay).fill(null);
                                for (let d = 1; d <= daysInMonth; d++) {
                                    week.push(d);
                                    if (week.length === 7) { weeks.push(week); week = []; }
                                }
                                if (week.length > 0) { while (week.length < 7) week.push(null); weeks.push(week); }
                                const monthName = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date(year, month, 1));
                                return (
                                    <div style={{ flex: '1 1 0' }}>
                                        <div style={{ textAlign: 'center', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.95rem' }}>{monthName} {year}</div>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                            <thead>
                                                <tr>{DAYS.map((d, i) => <th key={i} style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.75rem', color: 'var(--color-text-muted)', fontWeight: 500 }}>{d}</th>)}</tr>
                                            </thead>
                                            <tbody>
                                                {weeks.map((wk, wi) => (
                                                    <tr key={wi}>
                                                        {wk.map((day, di) => {
                                                            if (!day) return <td key={di}></td>;
                                                            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                                            const isStart = filterDateFrom === dateStr;
                                                            const isEnd = filterDateTo === dateStr;
                                                            const inRange = filterDateFrom && filterDateTo && dateStr > filterDateFrom && dateStr < filterDateTo;
                                                            const isToday = dateStr === new Date().toISOString().slice(0, 10);
                                                            let bg = 'transparent';
                                                            let color = 'inherit';
                                                            let fontWeight = 400;
                                                            let borderRadius = '50%';
                                                            if (isStart || isEnd) { bg = 'var(--color-primary, #4285f4)'; color = '#fff'; fontWeight = 700; }
                                                            else if (inRange) { bg = 'rgba(66,133,244,0.12)'; borderRadius = '0'; }
                                                            return (
                                                                <td key={di} style={{ padding: '1px', textAlign: 'center' }}>
                                                                    <div
                                                                        onClick={() => {
                                                                            if (datePickerStep === 'start') {
                                                                                setFilterDateFrom(dateStr);
                                                                                setFilterDateTo('');
                                                                                setDatePickerStep('end');
                                                                            } else {
                                                                                if (dateStr < filterDateFrom) { setFilterDateFrom(dateStr); setFilterDateTo(filterDateFrom); }
                                                                                else { setFilterDateTo(dateStr); }
                                                                                setDatePickerStep('start');
                                                                            }
                                                                        }}
                                                                        style={{ width: '32px', height: '32px', lineHeight: '32px', margin: '0 auto', borderRadius, background: bg, color, fontWeight, cursor: 'pointer', fontSize: '0.85rem', transition: 'background 0.15s', border: isToday && !isStart && !isEnd ? '1px solid var(--color-primary, #4285f4)' : 'none' }}
                                                                        onMouseOver={e => { if (!isStart && !isEnd && !inRange) e.currentTarget.style.background = 'rgba(66,133,244,0.08)'; }}
                                                                        onMouseOut={e => { if (!isStart && !isEnd && !inRange) e.currentTarget.style.background = 'transparent'; }}
                                                                    >
                                                                        {day}
                                                                    </div>
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            };
                            const m2 = calendarMonth === 11 ? 0 : calendarMonth + 1;
                            const y2 = calendarMonth === 11 ? calendarYear + 1 : calendarYear;
                            return (
                                <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 10, background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)', borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', padding: '1.25rem', marginTop: '4px', width: '520px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                                        <button onClick={() => { if (calendarMonth === 0) { setCalendarMonth(11); setCalendarYear(calendarYear - 1); } else setCalendarMonth(calendarMonth - 1); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: '0.25rem 0.5rem', color: 'var(--color-text-muted)' }}>❮</button>
                                        <span style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                                            {datePickerStep === 'start' ? 'Select start date' : 'Select end date'}
                                        </span>
                                        <button onClick={() => { if (calendarMonth === 11) { setCalendarMonth(0); setCalendarYear(calendarYear + 1); } else setCalendarMonth(calendarMonth + 1); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: '0.25rem 0.5rem', color: 'var(--color-text-muted)' }}>❯</button>
                                    </div>
                                    <div style={{ display: 'flex', gap: '1.5rem' }}>
                                        {renderMonth(calendarYear, calendarMonth)}
                                        {renderMonth(y2, m2)}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.75rem' }}>
                                        <button className="btn btn-secondary btn-sm" onClick={() => { setFilterDateFrom(''); setFilterDateTo(''); setDatePickerStep('start'); }}>Reset</button>
                                        <button className="btn btn-primary btn-sm" onClick={() => setShowDatePicker(false)}>Done</button>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {loading ? (
                    <p style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: '2rem' }}>Loading...</p>
                ) : sortedRecords.length === 0 ? (
                    <p style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: '2rem' }}>No records found.</p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid var(--color-primary)', textAlign: 'left' }}>
                                    <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)' }}>Date</th>
                                    <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)' }}>Name</th>
                                    {isAdmin && <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)' }}>Full Name</th>}
                                    <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)' }}>Tax Year</th>
                                    <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)', textAlign: 'right' }}>Transactions</th>
                                    {isAdmin && <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)', textAlign: 'right' }}>Tx Time</th>}
                                    <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)', textAlign: 'right' }}>Statements</th>
                                    {isAdmin && <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)', textAlign: 'right' }}>Stmt Time</th>}
                                    <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)', textAlign: 'right', fontWeight: 700 }}>Total Time</th>
                                    <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRecords.map((row, i) => {
                                    const tx = parseFloat(row.classifiedTransactions) || 0;
                                    const stmt = parseFloat(row.extractedBankStatement) || 0;
                                    const { txMinutes, stmtMinutes, totalMinutes } = calcMinutes(tx, stmt);
                                    return (
                                        <tr key={row.id || i} style={{ borderBottom: '1px solid var(--color-border)' }}>
                                            {editingId === row.id ? (
                                                <>
                                                    <td style={{ padding: '0.5rem' }}>
                                                        <input className="form-control" type="date" style={{ padding: '0.4rem' }} value={editForm.recordDate} onChange={e => setEditForm({ ...editForm, recordDate: e.target.value })} />
                                                    </td>
                                                    <td style={{ padding: '0.5rem' }}>
                                                        <input className="form-control" style={{ padding: '0.4rem' }} value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
                                                    </td>
                                                    {isAdmin && <td style={{ padding: '0.5rem', color: 'var(--color-text-muted)' }}>{resolveFullName(editForm.name)}</td>}
                                                    <td style={{ padding: '0.5rem' }}>
                                                        <input className="form-control" style={{ padding: '0.4rem', width: '80px' }} value={editForm.taxYear} onChange={e => setEditForm({ ...editForm, taxYear: e.target.value })} />
                                                    </td>
                                                    <td style={{ padding: '0.5rem' }}>
                                                        <input className="form-control" style={{ padding: '0.4rem' }} value={editForm.classifiedTransactions} onChange={e => setEditForm({ ...editForm, classifiedTransactions: e.target.value })} />
                                                    </td>
                                                    {isAdmin && <td style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--color-teal)' }}>—</td>}
                                                    <td style={{ padding: '0.5rem' }}>
                                                        <input className="form-control" style={{ padding: '0.4rem' }} value={editForm.extractedBankStatement} onChange={e => setEditForm({ ...editForm, extractedBankStatement: e.target.value })} />
                                                    </td>
                                                    {isAdmin && <td style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--color-teal)' }}>—</td>}
                                                    <td style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--color-primary)' }}>—</td>
                                                    <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>
                                                        <button className="btn btn-link btn-link-primary" onClick={() => saveEdit(row.id)}>Save</button>
                                                        <button className="btn btn-link" style={{ color: 'var(--color-gray)' }} onClick={cancelEdit}>Cancel</button>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td style={{ padding: '0.75rem 0.5rem', fontSize: '0.85rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{formatDate(row.recordDate)}</td>
                                                    <td style={{ padding: '0.75rem 0.5rem', fontWeight: 500 }}>{row.name}</td>
                                                    {isAdmin && <td style={{ padding: '0.75rem 0.5rem', color: 'var(--color-text-muted)' }}>{resolveFullName(row.name)}</td>}
                                                    <td style={{ padding: '0.75rem 0.5rem' }}>{row.taxYear}</td>
                                                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{tx || '—'}</td>
                                                    {isAdmin && <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', color: 'var(--color-teal)' }}>{formatTime(txMinutes)}</td>}
                                                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{stmt || '—'}</td>
                                                    {isAdmin && <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', color: 'var(--color-teal)' }}>{formatTime(stmtMinutes)}</td>}
                                                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', fontWeight: 700, color: 'var(--color-primary)' }}>{formatTime(totalMinutes)}</td>
                                                    <td style={{ padding: '0.75rem 0.5rem', whiteSpace: 'nowrap' }}>
                                                        <button className="btn btn-link btn-link-primary" onClick={() => startEdit(row)}>Edit</button>
                                                        <button className="btn btn-link btn-link-danger" onClick={() => deleteRecord(row.id)}>Delete</button>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr style={{ borderTop: '2px solid var(--color-primary)', fontWeight: 700 }}>
                                    <td colSpan={isAdmin ? 4 : 3} style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)' }}>Grand Total</td>
                                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{grandTotals.tx || '—'}</td>
                                    {isAdmin && <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', color: 'var(--color-teal)' }}>{formatTime(grandTotals.txMin)}</td>}
                                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{grandTotals.stmt || '—'}</td>
                                    {isAdmin && <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', color: 'var(--color-teal)' }}>{formatTime(grandTotals.stmtMin)}</td>}
                                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', color: 'var(--color-primary)' }}>{formatTime(grandTotals.totalMin)}</td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}

                {/* Conversion rates - admin only */}
                {isAdmin && (
                    <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: 'var(--color-text-muted)', borderTop: '1px solid var(--color-border)', paddingTop: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {editingRates ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <strong>Conversion rates:</strong>
                                <input type="number" className="form-control" value={editTxPerMin} onChange={e => setEditTxPerMin(parseFloat(e.target.value) || 0)} style={{ width: '70px', padding: '0.3rem', fontSize: '0.85rem' }} min="0.1" step="0.1" />
                                <span>transactions/min •</span>
                                <input type="number" className="form-control" value={editMinPerStmt} onChange={e => setEditMinPerStmt(parseFloat(e.target.value) || 0)} style={{ width: '70px', padding: '0.3rem', fontSize: '0.85rem' }} min="0.1" step="0.1" />
                                <span>min/statement</span>
                                <button className="btn btn-primary btn-sm" onClick={async () => {
                                    try {
                                        await setDoc(doc(db, 'settings', 'conversionRates'), { txPerMin: editTxPerMin, minPerStmt: editMinPerStmt });
                                        setTxPerMin(editTxPerMin);
                                        setMinPerStmt(editMinPerStmt);
                                        setEditingRates(false);
                                        setMessage('Conversion rates updated!');
                                        setTimeout(() => setMessage(''), 3000);
                                    } catch (e) { setMessage('Failed to save rates: ' + e.message); }
                                }}>Save</button>
                                <button className="btn btn-secondary btn-sm" onClick={() => { setEditTxPerMin(txPerMin); setEditMinPerStmt(minPerStmt); setEditingRates(false); }}>Cancel</button>
                            </div>
                        ) : (
                            <div>
                                <strong>Conversion rates:</strong> {txPerMin} transactions/min • {minPerStmt} min/statement
                            </div>
                        )}
                        {!editingRates && <button className="btn btn-secondary btn-sm" onClick={() => setEditingRates(true)}>Edit</button>}
                    </div>
                )}
            </div>
        </div>
    );
}

export default Hours;
